import { DataLayer, DataLayerEventCustomEvent, DataLayerItem } from '@pulse/browser-shared';
import { AnalyticsPlugin, PulseCommand, PulseMessagePriority } from '@pulse/common';
import { PulsePluginContext } from '../context';
import { PulseTracker } from '../tracker';
import { VisibilityChangePlugin, VisibilityState } from './visibility-change.plugin';

/**
 * Tracks analytics messages produced to the dataLayer.
 */
export class DataLayerPlugin implements AnalyticsPlugin {
    private defaultPush?: (...items: DataLayerItem[]) => number;
    private tracker?: PulseTracker;

    constructor(
        private dataLayer: DataLayer,
        private commandPrefix: string,
        private visibilityChangePlugin: VisibilityChangePlugin,
    ) {}

    init(context: PulsePluginContext) {
        this.tracker = context.tracker as PulseTracker;

        if (!Array.isArray(this.dataLayer)) {
            throw new Error('DataLayer must be an array!');
        }

        this.defaultPush = this.dataLayer.push.bind(this.dataLayer);
        this.dataLayer.push = this.onPush.bind(this);

        this.readFromBeginning();
    }

    private onPush(...items: DataLayerItem[]): number {
        for (const item of items) {
            this.processItem(item);
        }

        if (this.defaultPush) {
            return this.defaultPush(...items);
        }

        return 0;
    }

    private readFromBeginning() {
        for (const item of this.dataLayer) {
            this.processItem(item);
        }
    }

    private processItem(item: DataLayerItem) {
        if (!this.tracker || typeof item !== 'object' || !item) {
            return;
        }

        const castedItem = item as DataLayerEventCustomEvent<Record<string, unknown>>;

        if (this.isCommand(item)) {
            const command: any = Array.from(item);
            this.tracker.cmd(command);
        } else if (item.pulse === 'event') {
            let priority;

            if (item.event_priority) {
                priority = item.event_priority;
                delete castedItem.event_priority;
            }
            const event = this.normalize(item);

            this.tracker.event(
                event,
                priority
                    ? priority
                    : this.visibilityChangePlugin.visibilityState === VisibilityState.Hidden
                      ? PulseMessagePriority.High
                      : PulseMessagePriority.Normal,
            );
        } else if (item.pulse === 'attribute') {
            const attributes = this.normalize(item);
            this.tracker.attribute(attributes as Record<string, unknown>);
        } else if (item.pulse === 'attribute_hints') {
            const attributes = this.normalize(item);
            this.tracker.attributeHint(attributes as Record<string, unknown>);
        } else if (item.pulse === 'state') {
            const state = this.normalize(item);
            this.tracker.set(state as Record<string, unknown>);
        } else if (
            castedItem.event === 'custom_event' ||
            castedItem.event === 'custom_event_non_interaction'
        ) {
            const priority = castedItem.event_priority;

            if (castedItem.event_priority) {
                delete castedItem.event_priority;
            }
            this.tracker.event(
                castedItem.data,
                priority === 2 ? PulseMessagePriority.High : PulseMessagePriority.Normal,
            );
        }
    }

    private isCommand(obj: any): obj is PulseCommand {
        return (
            (Array.isArray(obj) || Object.prototype.toString.call(obj) === '[object Arguments]') &&
            typeof obj[0] === 'string' &&
            obj[0].startsWith(this.commandPrefix)
        );
    }

    private normalize<T extends DataLayerItem>(item: T): T {
        const normalized = { ...item };
        delete (normalized as any).paa;
        if ((normalized as any).pulse) {
            delete (normalized as any).pulse;
        }

        return normalized;
    }
}
